/* eslint-disable no-alert */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Row, Col, Card, CardBody, CardTitle, Button, Badge,
} from 'reactstrap';
import { withTranslation } from 'react-i18next';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import {
  Formik, Form,
} from 'formik';

import BaseGrid from '../../components/Common/BaseGrid';

import { post } from '../../helpers/api_helper';
import { Can } from '../../utilities/Can';

function Consolidate(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
    timeOut: 8000,
  };

  const { t } = props;

  const [signal, setSignal] = useState(0);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedStock, setSelectedStock] = useState([]);

  useEffect(() => {
    setSignal(signal + 1);
  }, []);

  const generatePickRoute = async () => {
    try {
      const response = await post('/api/warehouse/putawayarea/consolidate', {
        area_ids: selectedAreas.map((area) => area.id),
        stock_ids: selectedStock,
      });

      toastr.success(t(`Pick route #${response.id} generated successfully`));
      setSelectedAreas([]);
      setSelectedStock([]);
      setSignal(signal + 1);
    } catch (error) {
      toastr.error(error.message);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        {selectedAreas.length > 0 && (
          <Row>
            <Col xl="4">
              <Card>
                <CardBody>
                  <CardTitle className="h4">
                    <span className="mdi mdi-consolidate fs-5" />
                    {' '}
                    {t('Selected Areas/Stock')}
                  </CardTitle>

                  <div className="mt-2">
                    {selectedAreas.map((area) => (
                      <>
                        <Badge key={area.id} color="danger" className="bg-soft text-dark">
                          {area.name}
                        </Badge>
                        <br />
                      </>
                    ))}
                  </div>

                  <div className="hstack gap-2 mt-2">
                    <div>{selectedStock.join(', ')}</div>
                    {selectedStock.length > 0 && (
                      <Button color="link" size="sm" className="p-1 ps-0" onClick={() => setSelectedStock([])}>
                        {t('Clear')}
                      </Button>
                    )}
                  </div>

                  <div className="hstack gap-2 mt-3">
                    {selectedStock.length > 0 && (
                      <Button color="primary" size="sm" onClick={generatePickRoute}>
                        {t('Generate Pick Route')}
                      </Button>
                    )}
                    <Button color="soft-dark" size="sm" onClick={() => { setSelectedAreas([]); setSelectedStock([]); setSignal(signal + 1); }}>
                      {t('Clear')}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        <Row>
          <Col xl="6">
            <Card>
              <CardBody>
                <CardTitle className="h4">
                  <span className="mdi mdi-consolidate fs-5" />
                  {' '}
                  {t('Areas')}
                </CardTitle>

                <Can i="see" this="putaway">
                  <Formik>
                    <Form>
                      <BaseGrid
                        url="/api/warehouse/putawayarea"
                        shouldUpdate={signal}
                        hasSearch
                        config={
                          {
                            columns: {
                              actions: {
                                label: 'Actions',
                                actions: [
                                  {
                                    condition: (area) => !selectedAreas
                                      .some((a) => a.id === area.id),
                                    key: 'edit_area',
                                    icon: 'mdi mdi-checkbox-blank-outline',
                                    class: 'btn btn-sm btn-soft-dark fs-4 text-white p-1',
                                    actionStyle: { lineHeight: '20px' },
                                    alt: 'Edit Area',
                                    click: (area) => {
                                      const areaExists = selectedAreas
                                        .some((a) => a.id === area.id);
                                      if (areaExists) {
                                        setSelectedAreas(selectedAreas
                                          .filter((a) => a.id !== area.id));
                                      } else {
                                        setSelectedAreas([...selectedAreas, area]);
                                      }
                                    },
                                  },
                                  {
                                    condition: (area) => selectedAreas
                                      .some((a) => a.id === area.id),
                                    key: 'edit_area',
                                    icon: 'mdi mdi-checkbox-marked-outline',
                                    class: 'btn btn-sm btn-warning fs-4 text-dark p-1',
                                    actionStyle: { lineHeight: '20px' },
                                    alt: 'Edit Area',
                                    click: (area) => {
                                      const areaExists = selectedAreas
                                        .some((a) => a.id === area.id);
                                      if (areaExists) {
                                        setSelectedAreas(selectedAreas
                                          .filter((a) => a.id !== area.id));
                                      } else {
                                        setSelectedAreas([...selectedAreas, area]);
                                      }
                                    },
                                  },
                                ],
                              },
                              id: {
                                label: 'ID',
                                sort: true,
                              },
                              name: {
                                label: t('Name'),
                              },
                              lot_query: {
                                label: t('Lots'),
                                style: { maxWidth: '200px' },
                              },
                            },
                            pagination: {
                              sort: 'id',
                              direction: 'desc',
                              perPage: 30,
                            },
                          }
                        }
                      />
                    </Form>
                  </Formik>

                </Can>

                <Can not i="see" this="putaway"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>
          </Col>

          <Col xl="6">
            <Card>
              <CardBody>
                <CardTitle className="h4">
                  <span className="mdi mdi-consolidate fs-5" />
                  {' '}
                  {t('Stock')}
                </CardTitle>

                <Can i="see" this="putaway">
                  <Formik>
                    <Form>
                      <BaseGrid
                        url="/api/warehouse/putawayarea/fragments"
                        filters={selectedAreas.map((a) => a.id).join(',')}
                        shouldUpdate={signal}
                        hasSearch
                        config={
                          {
                            columns: {
                              actions: {
                                label: 'Actions',
                                actions: [
                                  {
                                    condition: (data) => !selectedStock.includes(data.stock_id),
                                    key: 'add_stock',
                                    icon: 'mdi mdi-checkbox-blank-outline',
                                    class: 'btn btn-sm btn-soft-dark fs-4 text-white p-1',
                                    actionStyle: { lineHeight: '20px' },
                                    alt: 'Add Stock',
                                    click: (data) => {
                                      // act like checkbox, if checked, add to list,
                                      // if unchecked, remove from list

                                      if (selectedStock.includes(data.stock_id)) {
                                        setSelectedStock(selectedStock
                                          .filter((id) => id !== data.stock_id));
                                      } else {
                                        setSelectedStock([...selectedStock, data.stock_id]);
                                      }
                                    },
                                  },
                                  {
                                    condition: (data) => selectedStock.includes(data.stock_id),
                                    key: 'remove_stock',
                                    icon: 'mdi mdi-checkbox-marked-outline',
                                    class: 'btn btn-sm btn-warning fs-4 text-dark p-1',
                                    actionStyle: { lineHeight: '20px' },
                                    alt: 'Remove Stock',
                                    click: (data) => {
                                      // act like checkbox, if checked, add to list,
                                      // if unchecked, remove from list
                                      if (selectedStock.includes(data.stock_id)) {
                                        setSelectedStock(selectedStock
                                          .filter((id) => id !== data.stock_id));
                                      } else {
                                        setSelectedStock([...selectedStock, data.stock_id]);
                                      }
                                    },
                                  },
                                ],
                              },
                              stock_id: {
                                label: 'StockID',
                                sort: true,
                              },
                              part_number: {
                                label: t('Part Number'),
                                format: (x, ent) => x ?? ent.sku,
                              },
                              location_count: {
                                label: t('Locs'),
                                sort: true,
                              },
                              total_qty: {
                                label: t('Qty'),
                                sort: true,
                              },
                            },
                            pagination: {
                              sort: 'id',
                              direction: 'desc',
                              perPage: 20,
                            },
                          }
                        }
                      />
                    </Form>
                  </Formik>

                </Can>

                <Can not i="see" this="putaway"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

Consolidate.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(Consolidate));
